// This file holds generic actions which doesn't
// fit into or relate to a specific component like process/swimlane/step/field/rule

import type {
  Field,
  Process,
  ProcessProps,
  Step,
  Swimlane
} from "@certa/studio-core";
import {
  ComponentKey,
  duplicateComponent as duplicateComponentUtil
} from "@certa/studio-core";

import { dbOps } from "../hooks/dbOps.store";
import { getMetaJSONFromDB } from "../../../utils/processUtils";
import { getParentComponentType } from "./common.utils";
import { Intents } from "@certa/studio-core/src/constants/activityLogs.constants";

const saveDuplicatedComponent = ({
  processId,
  originalComponentId,
  duplicatedComponentId,
  parentComponentId,
  parentComponentType,
  metaJSON
}: {
  processId: string;
  originalComponentId: string;
  duplicatedComponentId: string;
  parentComponentId: string;
  parentComponentType: ComponentKey;
  metaJSON: Partial<Process>;
}) => {
  let intent;

  if (parentComponentType === ComponentKey.PROCESS) {
    intent = Intents.DUPLICATE_SWIMLANE;
  } else if (parentComponentType === ComponentKey.SWIMLANES) {
    intent = Intents.DUPLICATE_STEP;
  } else if (parentComponentType === ComponentKey.STEPS) {
    intent = Intents.DUPLICATE_FIELD;
  }

  if (!intent) throw new Error("Invalid parent component type");

  try {
    return dbOps().transaction(
      () => {
        // Putting the duplicated component Right after the original component
        // that was duplicated
        let parentComponent;

        if (parentComponentType === ComponentKey.SWIMLANES) {
          parentComponent = dbOps().getSwimlane(processId, parentComponentId);
          if (!parentComponent) return;
          const originalIndex =
            parentComponent?.steps.indexOf(originalComponentId);
          parentComponent?.steps.splice(
            originalIndex + 1,
            0,
            duplicatedComponentId
          );
          dbOps().setSwimlane(processId, parentComponent);
        } else if (parentComponentType === ComponentKey.STEPS) {
          parentComponent = dbOps().getStep(processId, parentComponentId);
          if (!parentComponent) return;

          const originalIndex =
            parentComponent?.fields.indexOf(originalComponentId);
          parentComponent?.fields.splice(
            originalIndex + 1,
            0,
            duplicatedComponentId
          );
          dbOps().setStep(processId, parentComponent);
        } else if (parentComponentType === ComponentKey.PROCESS) {
          parentComponent = dbOps().getProcess(processId);
          if (!parentComponent) return;

          const originalIndex =
            parentComponent?.swimlaneSequence.indexOf(originalComponentId);
          parentComponent?.swimlaneSequence.splice(
            originalIndex + 1,
            0,
            duplicatedComponentId
          );
          dbOps().setProcess(processId, parentComponent);
        }

        // Now adding the rest of the stuff to the db

        const swimlanes = Object.values(metaJSON?.swimlanes || {});
        const steps = Object.values(metaJSON?.steps || {});
        const fields = Object.values(metaJSON?.fields || {});
        const rules = Object.values(metaJSON?.rules || {});
        const conditionGroups = Object.values(metaJSON?.conditionGroups || {});
        const conditions = Object.values(metaJSON?.conditions || {});

        if (swimlanes.length) {
          dbOps().bulkSetSwimlanes(processId, swimlanes);
        }
        if (steps.length) {
          dbOps().bulkSetSteps(processId, steps);
        }
        if (fields.length) {
          dbOps().bulkSetFields(processId, fields);
        }
        if (rules.length) {
          dbOps().bulkSetRules(processId, rules);
        }
        if (conditionGroups.length) {
          dbOps().bulkSetConditionGroups(processId, conditionGroups);
        }
        if (conditions.length) {
          dbOps().bulkSetConditions(processId, conditions);
        }

        return true;
      },
      { intent }
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      "There was an error occurred while saving component tree to DB",
      e
    );
    return false;
  }
};

export const duplicateComponent = ({
  processId,
  componentId,
  componentType,
  parentComponentId
}: {
  processId: string;
  componentId: string;
  componentType:
    | ComponentKey.SWIMLANES
    | ComponentKey.STEPS
    | ComponentKey.FIELDS;
  parentComponentId: string;
}) => {
  const componentMetaJSON = getMetaJSONFromDB({
    processId,
    componentId,
    componentType
  }) as Partial<Process>;

  const result = duplicateComponentUtil({
    componentMetaJSON,
    componentId,
    componentType
  });

  if (!result)
    throw new Error("There was an error while duplicating the component");

  const parentComponentType = getParentComponentType(componentType);
  if (!parentComponentType)
    throw new Error(`Invalid component type: ${componentType}`);

  saveDuplicatedComponent({
    processId,
    ...result,
    parentComponentId,
    parentComponentType
  });
};

export const updateComponent = (props: {
  processId: string;
  componentType: ComponentKey;
  componentId: string;
  componentProps:
    | Partial<ProcessProps>
    | Partial<Swimlane>
    | Partial<Step>
    | Partial<Field>;
}) => {
  const { processId, componentType, componentId, componentProps } = props;

  let operationFn: (
    processId: string,
    componentId: string,
    componentProps: any
  ) => void;
  let intent;

  // Not using update functions from other actions, as it was causing circular deps
  switch (componentType) {
    case ComponentKey.SWIMLANES:
      intent = Intents.UPDATE_SWIMLANE;
      operationFn = dbOps().updateSwimlane;
      break;
    case ComponentKey.STEPS:
      intent = Intents.UPDATE_STEP;
      operationFn = dbOps().updateStep;
      break;
    case ComponentKey.FIELDS:
      intent = Intents.UPDATE_FIELD;
      operationFn = dbOps().updateField;
      break;
    case ComponentKey.PROCESS:
      intent = Intents.UPDATE_PROCESS;
      operationFn = (
        _: string,
        processId: string,
        processProps: Partial<ProcessProps>
      ) => dbOps().updateProcess(processId, processProps);
      break;
  }

  dbOps().transaction(
    () => operationFn(processId, componentId, componentProps),
    { intent }
  );
};

export const setComponent = (
  props: { processId: string; branchSavepointId: number } & (
    | {
        componentType: ComponentKey.PROCESS;
        componentData: ProcessProps;
      }
    | {
        componentType: ComponentKey.SWIMLANES;
        componentData: Swimlane;
      }
    | {
        componentType: ComponentKey.STEPS;
        componentData: Step;
      }
    | {
        componentType: ComponentKey.FIELDS;
        componentData: Field;
      }
  )
) => {
  const { processId, componentType, componentData } = props;

  let operationFn: (processId: string, componentData: any) => void;
  let intent;

  switch (componentType) {
    case ComponentKey.SWIMLANES:
      intent = Intents.UPDATE_SWIMLANE;
      operationFn = dbOps().setSwimlane;
      break;
    case ComponentKey.STEPS:
      intent = Intents.UPDATE_STEP;
      operationFn = dbOps().setStep;
      break;
    case ComponentKey.FIELDS:
      intent = Intents.UPDATE_FIELD;
      operationFn = dbOps().setField;
      break;
    case ComponentKey.PROCESS:
      intent = Intents.UPDATE_PROCESS;
      operationFn = dbOps().setProcess;
      break;
  }

  dbOps().transaction(
    () => {
      operationFn(processId, componentData);
    },
    {
      intent
    }
  );
};
